import React, { Fragment, useEffect } from 'react';
import { Link as RouterLink, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    IconButton,
    Skeleton,
    styled,
    Tooltip,
    Typography,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiSwapHorizontalVariant } from '@mdi/js';

import { publicRoutes } from '../../../routes/appRouteList';
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import { clearLaneDetailsData } from '../../../state/features/laneDetailSlice';
import { ApiStatus } from '../../../utils/enums';
import { buildAreaLink, buildLaneLink } from '../../../utils/navigationHelpers';
import { useFilters } from '../../Filters/useFilters';
import DetailHeader from './DetailHeader';

const classesPrefix = 'laneDetailsHeader';

const classes = {
    headerTitle: `${classesPrefix}-headerTitle`,
    loading: `${classesPrefix}-loading`,
    link: `${classesPrefix}-link`,
    icon: `${classesPrefix}-icon`,
    title: `${classesPrefix}-title`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.headerTitle}`]: {
            display: 'flex',
            alignItems: 'center',
            '@media (max-width: 640px)': {
                flexWrap: 'wrap',
                justifyContent: 'center',
            },
        },
        [`& .${classes.loading}`]: {
            backgroundColor: theme.palette.grey[100],
        },
        [`& .${classes.link}`]: {
            cursor: 'pointer',
            color: theme.palette.getContrastText(theme.palette.primary.main),
            textDecoration: 'none',
            '&:hover': {
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.common.white,
            },
            padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
            borderRadius: theme.spacing(2),
        },
        [`& .${classes.icon}`]: {
            padding: '2px',
            '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.3)',
            },
        },
        [`& .${classes.title}`]: {
            whiteSpace: 'nowrap',
        },
    };
});

const LaneDetailsHeader = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const filters = useFilters();

    const laneDetailsApiStatus = useAppSelector((state) => { return state.lane.laneDetailsApiStatus; });
    const laneDetails = useAppSelector((state) => { return state.lane.laneDetails; });

    const originParam = searchParams.get('origin');
    const destParam = searchParams.get('dest');
    const origin = laneDetails.pickup_service_area_name || originParam;
    const destination = laneDetails.delivery_service_area_name || destParam;

    useEffect(() => {
        return () => {
            dispatch(clearLaneDetailsData());
        };
    }, [dispatch]);

    if (laneDetailsApiStatus === ApiStatus.Failure) {
        return (
            <Navigate
                to={`${publicRoutes.notFound}?requestedroute=${encodeURIComponent(location.pathname + location.search)}&reason=${encodeURIComponent(`We tried, but we couldn't find a lane from ${originParam} to ${destParam}.`)}`}
            />
        );
    }

    return (
        <DetailHeader
            onBackClicked={(): void => {
                navigate(-1);
            }}
        >
            <StyledDiv className={classes.headerTitle}>
                {laneDetailsApiStatus === ApiStatus.Idle || laneDetailsApiStatus === ApiStatus.Loading ? (
                    <Typography variant='h6'>
                        <Skeleton className={classes.loading} width='180px' />
                    </Typography>
                ) : (
                    <Fragment>
                        <Typography variant='h6' noWrap>
                            <Tooltip title='View Area' placement='top'>
                                <RouterLink
                                    className={classes.link}
                                    to={buildAreaLink(laneDetails.pickup_service_area_id || originParam || '', filters)}
                                >
                                    {origin}
                                </RouterLink>
                            </Tooltip>
                        </Typography>
                        <Tooltip title='Switch origin and destination' placement='top'>
                            <IconButton
                                className={`${classes.link} ${classes.icon}`}
                                component={RouterLink}
                                to={buildLaneLink(destParam || '', originParam || '', filters)}
                                size='small'
                                disabled={origin === destination}
                            >
                                <Icon path={mdiSwapHorizontalVariant} size={1} />
                            </IconButton>
                        </Tooltip>
                        <Typography variant='h6' noWrap>
                            <Tooltip title='View Area' placement='top'>
                                <RouterLink
                                    className={`${classes.link} ${classes.title}`}
                                    to={buildAreaLink(laneDetails.delivery_service_area_id || destParam || '', filters)}
                                >
                                    {destination}
                                </RouterLink>
                            </Tooltip>
                        </Typography>
                    </Fragment>
                )}
            </StyledDiv>
        </DetailHeader>
    );
};

export default LaneDetailsHeader;
